/**
 * Shared utilities for resolving relationships between orgs and enterprise organizations.
 * Cardinality: enterprise_org (1) → org (N) → theme (0..1).
 * Uses the org_enterprise_config bridge table as the single source of truth.
 */

import { SupabaseClient } from "@supabase/supabase-js";

import { unwrapToOne } from "@/lib/utils/postgrest";

/**
 * Resolve an org_id to its enterprise_organization_id via org_enterprise_config.
 * @param supabase - Any Supabase client (user-scoped or service-role)
 * @param orgId - The org_id to look up
 * @returns The enterprise_organization_id if found, or null
 */
export async function resolveEnterpriseOrgId(supabase: SupabaseClient, orgId: number): Promise<number | null> {
  const { data } = await supabase
    .schema("nowadays")
    .from("org_enterprise_config")
    .select("enterprise_organization_id")
    .eq("org_id", orgId)
    .maybeSingle();

  return data?.enterprise_organization_id ?? null;
}

/**
 * Resolve ALL org_ids linked to an enterprise_organization_id via org_enterprise_config.
 * @param supabase - Any Supabase client (user-scoped or service-role)
 * @param enterpriseOrgId - The enterprise_organization_id to look up
 * @returns Array of org_ids (may be empty)
 */
export async function resolveAllOrgIdsFromEnterpriseOrg(
  supabase: SupabaseClient,
  enterpriseOrgId: number
): Promise<number[]> {
  const { data } = await supabase
    .schema("nowadays")
    .from("org_enterprise_config")
    .select("org_id")
    .eq("enterprise_organization_id", enterpriseOrgId);

  return (data ?? []).map((c) => c.org_id).filter((id): id is number => id != null);
}

/**
 * Check which enterprise_organization_id an org currently belongs to via org_enterprise_config.
 * Each org belongs to at most one enterprise org (N:1 cardinality).
 * @param supabase - Any Supabase client (user-scoped or service-role)
 * @param orgId - The org_id to check
 * @returns The enterprise_organization_id the org belongs to, or null if unlinked
 */
export async function getOrgEnterpriseOwner(supabase: SupabaseClient, orgId: number): Promise<number | null> {
  return resolveEnterpriseOrgId(supabase, orgId);
}

/**
 * Resolve the enterprise org that owns a group.
 * @param supabase - Any Supabase client (user-scoped or service-role)
 * @param groupId - The group to look up
 * @returns `enterprise_groups.organization_id`, or null if the group doesn't exist.
 */
export async function resolveOrgForGroup(supabase: SupabaseClient, groupId: number): Promise<number | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("enterprise_groups")
    .select("organization_id")
    .eq("id", groupId)
    .maybeSingle();

  if (error) {
    console.error("resolveOrgForGroup: lookup failed", { groupId }, error);
    return null;
  }
  return (data as { organization_id: number | null } | null)?.organization_id ?? null;
}

/**
 * Resolve the enterprise org that owns an event, via `events.org_id -> org_enterprise_config`.
 * @param supabase - Any Supabase client (user-scoped or service-role)
 * @param eventId - The event to look up
 * @returns The `enterprise_organization_id`, or null if the event or its org-link doesn't exist.
 */
export async function resolveOrgForEvent(supabase: SupabaseClient, eventId: number): Promise<number | null> {
  const { data: event, error } = await supabase
    .schema("nowadays")
    .from("events")
    .select("org_id")
    .eq("event_id", eventId)
    .maybeSingle();

  if (error) {
    console.error("resolveOrgForEvent: lookup failed", { eventId }, error);
    return null;
  }
  const orgId = (event as { org_id: number | null } | null)?.org_id;
  if (!orgId) return null;
  return resolveEnterpriseOrgId(supabase, orgId);
}

/**
 * Resolve every legacy `org_id` in an org's enterprise scope: the org itself, plus every other org
 * linked to the same enterprise org (`org_enterprise_config`). Shared by `isUserInOrgScope` and any
 * other caller that needs an org's full scope (e.g. an existence check rather than a membership
 * check), so the definition of "same scope" can never drift between them.
 *
 * Pass a SERVICE-ROLE client — see `isUserInOrgScope`'s client note; the same RLS gap applies here.
 *
 * @param supabase - A service-role Supabase client.
 * @param orgId - The org whose scope to resolve. An org with no enterprise bridge is its own scope.
 * @returns Every `org_id` in scope, including `orgId` itself.
 * @throws When the bridge lookup fails. Never degrades to a narrower scope on error, because that
 *   would silently change the answer for every caller of this shared resolution.
 */
export async function resolveOrgScopeIds(supabase: SupabaseClient, orgId: number): Promise<number[]> {
  const { data: bridge, error: bridgeError } = await supabase
    .schema("nowadays")
    .from("org_enterprise_config")
    .select("enterprise_organization_id")
    .eq("org_id", orgId)
    .maybeSingle();

  if (bridgeError) throw bridgeError;

  const enterpriseOrgId = bridge?.enterprise_organization_id ?? null;
  const linkedOrgIds = enterpriseOrgId ? await resolveAllOrgIdsFromEnterpriseOrg(supabase, enterpriseOrgId) : [];
  return Array.from(new Set([orgId, ...linkedOrgIds]));
}

/**
 * Whether a user belongs to an org's scope (`resolveOrgScopeIds`). This is what "belongs to the
 * event's org" means for the event-ownership feature. It backs both the preview
 * (`prepareOwnerChange`) and the server-side enforcement (`setEventOwner`), so the two cannot drift
 * apart.
 *
 * @param supabase - A service-role Supabase client (see `resolveOrgScopeIds`).
 * @param orgId - The org whose scope to check.
 * @param userId - The user to check.
 * @returns Whether the user belongs to any org in scope.
 * @throws When the scope lookup or the membership lookup fails. Callers decide how to surface that
 *   (a soft `{ success: false }` for a preview, or refusing the mutation for an enforcement check).
 *   It never degrades to a narrower scope on error, because that would silently change the answer.
 */
export async function isUserInOrgScope(supabase: SupabaseClient, orgId: number, userId: number): Promise<boolean> {
  const scopeOrgIds = await resolveOrgScopeIds(supabase, orgId);

  const { data, error } = await supabase
    .schema("nowadays")
    .from("org_users")
    .select("user_id")
    .eq("user_id", userId)
    .in("org_id", scopeOrgIds)
    .limit(1);

  if (error) throw error;
  return (data?.length ?? 0) > 0;
}

/**
 * Ensure a user belongs to every legacy org backing an enterprise org (`org_users`).
 * Adding a user to an enterprise group or event should also grant org-level
 * membership. Upserts with `ignoreDuplicates` to stay idempotent without a
 * TOCTOU check. Best-effort: logs failures but does not throw so the caller's
 * primary flow (the actual grant) still succeeds.
 *
 * @param supabase - Service-role Supabase client.
 * @param userId - The user to add to the org(s).
 * @param enterpriseOrgId - `enterprise_organizations.id` whose orgs to join.
 */
export async function ensureUserInEnterpriseOrgs(
  supabase: SupabaseClient,
  userId: number,
  enterpriseOrgId: number
): Promise<void> {
  const orgIds = await resolveAllOrgIdsFromEnterpriseOrg(supabase, enterpriseOrgId);
  if (orgIds.length === 0) return;

  const rows = orgIds.map((org_id) => ({ user_id: userId, org_id }));
  const { error } = await supabase
    .schema("nowadays")
    .from("org_users")
    .upsert(rows, { onConflict: "user_id,org_id", ignoreDuplicates: true });

  if (error) {
    console.error("ensureUserInEnterpriseOrgs:", error);
  }
}
